import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconX, IconPlus, IconPencil, IconTrash, IconCheck,
  IconStar, IconStarFilled, IconShare, IconLock, IconWorld, IconUsers,
  IconTag,
} from "@tabler/icons-react";
import PipelineTagsPresetSection from "./PipelineTagsPresetSection";
import Swal from "sweetalert2";
import { useCRM } from "../CRMContext";
import { createPipeline, updatePipeline, deletePipeline, sharePipeline } from "../services/pipeline.service";
import { listUsers } from "../../../../screens/Users/services/UserServicesV2";
import UserContext from "../../../../contexts/UserContext";
import ProBadge from "../../../../screens/AgentsFlow/components/ProBadge";

const SHARING_LABELS = {
  private: { label: "Privado", icon: IconLock, color: "#64748b" },
  account: { label: "Conta inteira", icon: IconWorld, color: "#3b82f6" },
  specific: { label: "Usuários", icon: IconUsers, color: "#8b5cf6" },
};

const PipelineManagementModal = ({ onClose }) => {
  const { state, loadPipelines, switchPipeline, showToast } = useCRM();
  const { getUserPlan, isSubaccount, getOrganizationInfo } = useContext(UserContext);
  const navigate = useNavigate();

  const effectivePlan = (() => {
    if (isSubaccount()) {
      const orgInfo = getOrganizationInfo();
      return String(orgInfo?.plan_id || "");
    }
    return String(getUserPlan());
  })();
  const canCreatePipeline = effectivePlan === "2" || effectivePlan === "5";

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newIsDefault, setNewIsDefault] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  // Sharing state
  const [sharingPipelineId, setSharingPipelineId] = useState(null);
  const [sharingType, setSharingType] = useState("private");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [accountUsers, setAccountUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Tags preset state
  const [tagsPipelineId, setTagsPipelineId] = useState(null);

  // Carregar usuários da mesma conta quando abrir compartilhamento específico
  const { user: currentUser } = useContext(UserContext);
  const currentAccountId = currentUser?.account_id;

  useEffect(() => {
    if (sharingType === "specific" && accountUsers.length === 0) {
      setLoadingUsers(true);
      listUsers()
        .then((res) => {
          const allUsers = res?.data?.results || res?.data || [];
          // Filtrar apenas usuários da mesma conta (exclui subcontas e o próprio usuário)
          const sameAccountUsers = allUsers.filter(
            (u) => u.account === currentAccountId && u.id !== currentUser?.user_id
          );
          setAccountUsers(sameAccountUsers);
        })
        .catch(() => setAccountUsers([]))
        .finally(() => setLoadingUsers(false));
    }
  }, [sharingType, accountUsers.length, currentAccountId, currentUser?.user_id]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await createPipeline({ name: newName.trim(), is_default: newIsDefault });
      await loadPipelines();
      setNewName("");
      setNewIsDefault(false);
      setCreating(false);
      showToast("Pipeline criado!");
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || "Erro ao criar pipeline.";
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (pipeline) => {
    setEditingId(pipeline.id);
    setEditName(pipeline.name);
  };

  const handleSaveEdit = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await updatePipeline(id, { name: editName.trim() });
      await loadPipelines();
      setEditingId(null);
      showToast("Pipeline atualizado!");
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || "Erro ao atualizar pipeline.";
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDefault = async (pipeline) => {
    if (pipeline.is_default) return;
    setSaving(true);
    try {
      await updatePipeline(pipeline.id, { is_default: true });
      await loadPipelines();
      showToast("Pipeline padrão atualizado!");
    } catch (err) {
      showToast("Erro ao definir pipeline padrão.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (pipeline) => {
    if (pipeline.is_default) {
      showToast("Não é possível excluir o pipeline padrão.", "warning");
      return;
    }

    Swal.fire({
      title: "Confirmar exclusão",
      text: `Deseja excluir o pipeline "${pipeline.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deletePipeline(pipeline.id);
          await loadPipelines();
          if (state.activePipelineId === pipeline.id) {
            const remaining = state.pipelines.filter((p) => p.id !== pipeline.id);
            if (remaining.length > 0) switchPipeline(remaining[0].id);
          }
          showToast("Pipeline excluído!");
        } catch (err) {
          const msg = err?.response?.data?.error || err?.response?.data?.detail || "Erro ao excluir pipeline. Verifique se não há contatos associados.";
          showToast(msg, "error");
        }
      }
    });
  };

  // Sharing handlers
  const handleOpenSharing = (pipeline) => {
    setSharingPipelineId(pipeline.id);
    setSharingType(pipeline.sharing_type || "private");
    setSelectedUserIds((pipeline.shared_with || []).map((u) => (typeof u === "object" ? u.id : u)));
  };

  const handleSaveSharing = async () => {
    setSaving(true);
    try {
      const data = { sharing_type: sharingType };
      if (sharingType === "specific") {
        data.user_ids = selectedUserIds;
      }
      await sharePipeline(sharingPipelineId, data);
      await loadPipelines();
      setSharingPipelineId(null);
      showToast("Compartilhamento atualizado!");
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || "Erro ao compartilhar pipeline.";
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const getSharingBadge = (pipeline) => {
    const config = SHARING_LABELS[pipeline.sharing_type || "private"];
    const Icon = config.icon;
    return (
      <span
        title={config.label}
        style={{ color: config.color, display: "inline-flex", alignItems: "center" }}
      >
        <Icon size={15} />
      </span>
    );
  };

  return (
    <div className="crm-modal-backdrop" onClick={onClose}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="crm-modal__header">
          <h3 className="crm-modal__title">Gerenciar Pipelines</h3>
          <button className="crm-drawer__close" onClick={onClose}>
            <IconX size={20} />
          </button>
        </div>

        <div className="crm-modal__body">
          {/* Pipeline list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {state.pipelines.map((pipeline) => (
              <div key={pipeline.id}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: sharingPipelineId === pipeline.id ? "8px 8px 0 0" : 8,
                    border: "1px solid var(--crm-border, #e5e7eb)",
                    borderBottom: sharingPipelineId === pipeline.id ? "none" : undefined,
                    background: pipeline.id === state.activePipelineId ? "var(--crm-bg-active, #eff6ff)" : "transparent",
                  }}
                >
                  {/* Default star */}
                  <button
                    onClick={() => handleToggleDefault(pipeline)}
                    title={pipeline.is_default ? "Pipeline padrão" : "Definir como padrão"}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: pipeline.is_default ? "#f59e0b" : "#9ca3af" }}
                    disabled={saving}
                  >
                    {pipeline.is_default ? <IconStarFilled size={18} /> : <IconStar size={18} />}
                  </button>

                  {/* Name */}
                  {editingId === pipeline.id ? (
                    <div style={{ flex: 1, display: "flex", gap: 6 }}>
                      <input
                        className="crm-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSaveEdit(pipeline.id)}
                        autoFocus
                        style={{ flex: 1, padding: "4px 8px", fontSize: 14 }}
                      />
                      <button
                        className="crm-btn crm-btn--sm crm-btn--primary"
                        onClick={() => handleSaveEdit(pipeline.id)}
                        disabled={saving || !editName.trim()}
                      >
                        <IconCheck size={14} />
                      </button>
                      <button className="crm-btn crm-btn--sm" onClick={() => setEditingId(null)}>
                        <IconX size={14} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 500 }}>{pipeline.name}</span>
                      {getSharingBadge(pipeline)}
                      <span style={{ color: "#6b7280", fontSize: 12, marginLeft: 4 }}>
                        {pipeline.is_owner === false && pipeline.created_by_name
                          ? `por ${pipeline.created_by_name} · `
                          : ""
                        }
                        {pipeline.contacts_count || 0} contatos
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  {editingId !== pipeline.id && (
                    <div style={{ display: "flex", gap: 4 }}>
                      {pipeline.is_owner !== false && (
                        <button
                          className="crm-btn crm-btn--sm"
                          onClick={() =>
                            sharingPipelineId === pipeline.id
                              ? setSharingPipelineId(null)
                              : handleOpenSharing(pipeline)
                          }
                          title="Compartilhar"
                          disabled={saving}
                          style={{
                            color: sharingPipelineId === pipeline.id ? "#3b82f6" : undefined,
                          }}
                        >
                          <IconShare size={14} />
                        </button>
                      )}
                      {pipeline.is_owner !== false && (
                        <button
                          className="crm-btn crm-btn--sm"
                          onClick={() =>
                            tagsPipelineId === pipeline.id
                              ? setTagsPipelineId(null)
                              : setTagsPipelineId(pipeline.id)
                          }
                          title="Tags deste pipeline"
                          disabled={saving}
                          style={{
                            color: tagsPipelineId === pipeline.id ? "#3b82f6" : undefined,
                          }}
                        >
                          <IconTag size={14} />
                        </button>
                      )}
                      {pipeline.is_owner !== false && (
                        <button
                          className="crm-btn crm-btn--sm"
                          onClick={() => handleStartEdit(pipeline)}
                          title="Editar nome"
                          disabled={saving}
                        >
                          <IconPencil size={14} />
                        </button>
                      )}
                      {pipeline.is_owner !== false && (
                        <button
                          className="crm-btn crm-btn--sm crm-btn--danger"
                          onClick={() => handleDelete(pipeline)}
                          title={pipeline.is_default ? "Não é possível excluir o pipeline padrão" : "Excluir pipeline"}
                          disabled={saving || pipeline.is_default}
                        >
                          <IconTrash size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Tags preset panel */}
                {tagsPipelineId === pipeline.id && (
                  <PipelineTagsPresetSection
                    pipelineId={pipeline.id}
                    onClose={() => setTagsPipelineId(null)}
                  />
                )}

                {/* Sharing panel */}
                {sharingPipelineId === pipeline.id && (
                  <div
                    style={{
                      padding: 12,
                      border: "1px solid var(--crm-border, #e5e7eb)",
                      borderTop: "1px dashed var(--crm-border, #e5e7eb)",
                      borderRadius: "0 0 8px 8px",
                      background: "#f8fafc",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                      Compartilhamento
                    </div>

                    <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                      {Object.entries(SHARING_LABELS).map(([key, config]) => {
                        const Icon = config.icon;
                        const isActive = sharingType === key;
                        return (
                          <button
                            key={key}
                            onClick={() => setSharingType(key)}
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 4,
                              padding: "6px 8px",
                              borderRadius: 6,
                              border: isActive ? `2px solid ${config.color}` : "1px solid #d1d5db",
                              background: isActive ? `${config.color}10` : "#fff",
                              color: isActive ? config.color : "#6b7280",
                              fontSize: 11,
                              fontWeight: isActive ? 600 : 400,
                              cursor: "pointer",
                            }}
                          >
                            <Icon size={14} />
                            {config.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* User selection for specific */}
                    {sharingType === "specific" && (
                      <div style={{ marginBottom: 10 }}>
                        {loadingUsers ? (
                          <div style={{ fontSize: 12, color: "#6b7280", padding: 8 }}>Carregando...</div>
                        ) : (
                          <div
                            style={{
                              maxHeight: 150,
                              overflowY: "auto",
                              border: "1px solid #e5e7eb",
                              borderRadius: 6,
                              background: "#fff",
                            }}
                          >
                            {accountUsers.length === 0 ? (
                              <div style={{ padding: 8, fontSize: 12, color: "#6b7280" }}>
                                Nenhum usuário encontrado
                              </div>
                            ) : (
                              accountUsers.map((user) => (
                                <label
                                  key={user.id}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "6px 10px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #f3f4f6",
                                    fontSize: 13,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedUserIds.includes(user.id)}
                                    onChange={() => toggleUser(user.id)}
                                  />
                                  <span>
                                    {user.first_name || user.email}
                                    {user.last_name ? ` ${user.last_name}` : ""}
                                  </span>
                                  <span style={{ color: "#9ca3af", fontSize: 11, marginLeft: "auto" }}>
                                    {user.email}
                                  </span>
                                </label>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button
                        className="crm-btn crm-btn--sm"
                        onClick={() => setSharingPipelineId(null)}
                      >
                        Cancelar
                      </button>
                      <button
                        className="crm-btn crm-btn--sm crm-btn--primary"
                        onClick={handleSaveSharing}
                        disabled={saving || (sharingType === "specific" && selectedUserIds.length === 0)}
                      >
                        {saving ? "Salvando..." : "Salvar"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Create new pipeline form */}
          {creating ? (
            <div style={{ marginTop: 16, padding: 12, borderRadius: 8, border: "1px solid var(--crm-border, #e5e7eb)" }}>
              <div className="crm-form-group">
                <label className="crm-label">Nome do Pipeline</label>
                <input
                  className="crm-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  placeholder="Ex: Vendas B2B"
                  autoFocus
                />
              </div>
              <div style={{ marginTop: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={newIsDefault}
                    onChange={(e) => setNewIsDefault(e.target.checked)}
                  />
                  Definir como pipeline padrão
                </label>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  className="crm-btn crm-btn--primary crm-btn--sm"
                  onClick={handleCreate}
                  disabled={saving || !newName.trim()}
                >
                  {saving ? "Salvando..." : "Criar Pipeline"}
                </button>
                <button
                  className="crm-btn crm-btn--sm"
                  onClick={() => { setCreating(false); setNewName(""); setNewIsDefault(false); }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 16 }}>
              {canCreatePipeline ? (
                <button
                  className="crm-btn crm-btn--primary"
                  onClick={() => setCreating(true)}
                  disabled={saving}
                >
                  <IconPlus size={16} />
                  Novo Pipeline
                </button>
              ) : !isSubaccount() ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button className="crm-btn" disabled style={{ opacity: 0.5 }}>
                      Novo Pipeline
                    </button>
                    <ProBadge small />
                  </div>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Recurso disponível no plano Business.
                  </span>
                  <button
                    className="crm-btn crm-btn--primary crm-btn--sm"
                    onClick={() => { onClose(); navigate("/my-plan"); }}
                  >
                    Faça um upgrade
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="crm-modal__footer">
          <button className="crm-btn" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default PipelineManagementModal;
